import React from "react";

import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  StackDivider,
  Text,
  Tooltip
} from "@chakra-ui/react";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaArrowTrendUp, FaEnvelope, FaFileCsv, FaRegFilePdf } from "react-icons/fa6";

import "react-phone-input-2/lib/style.css";

import { useClientDashboard } from "@/hooks/features/useClientDashboard";
import { useCompanyDetails } from "@/hooks/features/useCompanyDetails";
import { useRangeSelector } from "@/hooks/util/useRangeSelector";
import { formatNumberWithThousandsSeparator } from "@/util/helpers";

import { CompaniesReport } from "./CompaniesReport";

export interface CompanyPractiseBillingProps {}

export const CompanyPractiseBilling: React.FC<
  CompanyPractiseBillingProps
> = () => {
  const { dateRange, monthsList, setDateRange, handleRangeReset } =
    useRangeSelector();
  const { dashboard: summary } = useClientDashboard(dateRange);
  const { company, isLoading } = useCompanyDetails();

  // Get billing rates from the company data
  const billingCurrency = company?.billing_currency || "GBP";
  const hourlyRate = company?.billing_hourly_rate || 21.60;
  const perTransactionRate = company?.billing_per_transaction_rate || "N/A";
  const currencySymbol = billingCurrency === "GBP" ? "£" : billingCurrency === "USD" ? "$" : "€";

  // Function to handle CSV download
  const handleCsvDownload = () => {
    // Create a simple CSV with the summary data
    const csvContent = [
      ["Billing Report", dateRange.label],
      ["Processed Invoices", summary?.total_processed || 0],
      ["Billable Amount (GBP)", summary?.billable_amount || 0],
      ["Extraction Time (secs)", summary?.extraction_duration || 0],
      ["Units", summary?.units || 0],
      ["Billing Currency", billingCurrency],
      ["Hourly Rate", hourlyRate],
      ["Transaction Rate", perTransactionRate]
    ]
      .map(row => row.join(","))
      .join("\n");
    
    // Create and trigger the download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    
    link.setAttribute("href", url);
    link.setAttribute("download", `billing_report_${dateRange.label.replace(/\s+/g, "_")}.csv`);
    document.body.appendChild(link);
    
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box pt={10}>
      <Box bg="white" rounded={16} px={8} py={8} w="full">
        <HStack spacing={4} justify="space-between">
          <Heading fontSize={20} fontWeight={600}>
            Billing
          </Heading>
          
          {/* Download options */}
          <HStack spacing={2}>
            <Button
              leftIcon={<FaFileCsv />}
              colorScheme="blue"
              size="sm"
              onClick={handleCsvDownload}
            >
              CSV
            </Button>
            
            <Tooltip label="PDF export is not available in this version" placement="top">
              <IconButton
                icon={<FaRegFilePdf />}
                colorScheme="blue"
                size="sm"
                variant="outline"
                isDisabled={true}
                aria-label="Download PDF"
              />
            </Tooltip>
            
            <Tooltip label="Email report is not available in this version" placement="top">
              <IconButton
                icon={<FaEnvelope />}
                colorScheme="blue"
                size="sm"
                variant="outline"
                isDisabled={true}
                aria-label="Email report"
              />
            </Tooltip>
          </HStack>
        </HStack>
        <Text
          color="secondary.700"
          fontSize={13}
          mt={2}
          fontWeight={500}
          opacity={0.7}
        >
          Shows Jack&apos;s extraction time and cost for all your clients
        </Text>

        {/* Billing Rates Section */}
        <Box mt={5} p={4} bg="gray.50" borderRadius="md">
          <Heading size="sm" mb={3}>
            Billing Rates
          </Heading>
          <Divider mb={3} />
          <Stack spacing={3}>
            <Flex justify="space-between">
              <Text fontSize="sm" fontWeight={500}>
                Billing Currency:
              </Text>
              <Badge fontSize="sm" colorScheme="blue">
                {billingCurrency}
              </Badge>
            </Flex>
            <Flex justify="space-between">
              <Text fontSize="sm" fontWeight={500}>
                Hourly Rate:
              </Text>
              <Text fontSize="sm" fontWeight={600}>
                {currencySymbol}
                {typeof hourlyRate === 'number' 
                  ? (hourlyRate as number).toFixed(2)
                  : hourlyRate}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text fontSize="sm" fontWeight={500}>
                Per-Transaction Rate:
              </Text>
              <Text fontSize="sm" fontWeight={600}>
                {perTransactionRate === "N/A" 
                  ? perTransactionRate 
                  : typeof perTransactionRate === 'number'
                    ? `${currencySymbol}${(perTransactionRate as number).toFixed(2)}`
                    : typeof perTransactionRate === 'string' && !isNaN(Number(perTransactionRate))
                      ? `${currencySymbol}${Number(perTransactionRate).toFixed(2)}`
                      : perTransactionRate}
              </Text>
            </Flex>
            <Text fontSize="xs" color="gray.500" mt={1}>
              These rates are configured by your service provider.
            </Text>
          </Stack>
        </Box>

        <Stack
          align={["flex-start", "flex-start", "flex-start", "center"]}
          mt={8}
          spacing={20}
          direction={["column", "column", "column", "row"]}
        >
          <Stack spacing={1}>
            <Text fontSize={13}>Billing Period</Text>
            <HStack spacing={3}>
              <Box>
                <Menu placement="bottom-start">
                  <MenuButton
                    variant="outline"
                    as={Button}
                    h={10}
                    w={261}
                    textAlign="left"
                    size="sm"
                    rightIcon={<ChevronDownIcon />}
                  >
                    {dateRange.label}
                  </MenuButton>
                  <MenuList>
                    {monthsList.map((month) => (
                      <MenuItem
                        fontWeight={500}
                        onClick={() => setDateRange(month)}
                        key={month.label}
                      >
                        {month.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>

              <Button
                onClick={handleRangeReset}
                variant="link"
                colorScheme="blue"
                size="sm"
              >
                Reset
              </Button>
            </HStack>
          </Stack>

          <Stack align="center" spacing={8} direction={["column", "row"]}>
            <Box rounded={8} bg="blackAlpha.20" py={4} px={8}>
              <Text fontWeight={500}>Processed Invoices</Text>

              <HStack spacing={4}>
                <Text fontSize={32} fontWeight={700} color="primary.500">
                  {summary ? summary.total_processed : 0}
                </Text>
                <Center boxSize={6} color="green.400" bg="green.50">
                  <Icon as={FaArrowTrendUp} />
                </Center>
              </HStack>
            </Box>

            <Box rounded={8} bg="blackAlpha.20" py={4} px={4}>
              <HStack>
                <Text fontWeight={500}>Billable Amount</Text>
              </HStack>

              <HStack align="baseline" spacing={2}>
                <Text fontSize={32} fontWeight={700} color="primary.500">
                  £
                  {summary?.billable_amount
                    ? formatNumberWithThousandsSeparator(
                        Number(summary.billable_amount)
                      )
                    : 0}
                </Text>
                <Text fontWeight={500}>GBP</Text>
              </HStack>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Stack
        mt={7}
        bg="white"
        w="full"
        rounded={6}
        border="1px solid"
        spacing={4}
        borderColor="blackAlpha.200"
        divider={<StackDivider />}
        py={6}
        px={10}
      >
        <HStack justify="space-between">
          <Text fontWeight={500} fontSize="sm">
            All Transactions
          </Text>

          <Badge
            color="primary.500"
            bg="primary.50"
            fontWeight={500}
            textTransform="initial"
            py={1}
            px={2}
          >
            {dateRange.label}
          </Badge>
        </HStack>

        <CompaniesReport summary={summary} />
      </Stack>
    </Box>
  );
};
